import classNames from 'classnames';
import { IconArrowDown } from 'components/icons/icon-arrow-down/icon-arrow-down';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router';
import * as R from 'ramda';
import Button from 'reactstrap/lib/Button';
import Collapse from 'reactstrap/lib/Collapse';

import {
  withCoreComponent,
  withModalActions,
  withBonusActions,
  withLocale,
  withUser
} from 'core/hocs';
import {
  PromoBonuses as PromoBonusesCore,
  FormattedTag,
  FormattedHTMLMessageSecure,
  RouteLink,
} from 'core/components';
import {
  isEmptyOrNil,
  getIntlKeyByEnv,
  wrapRoute2Locale,
  getIsAffiliate,
  getIsHtmlContentForbidden,
} from 'core/helpers';
import { USER_FIELDS } from 'core/constants';
import { Main } from 'components/main/main';
import { Carousel } from 'components/carousel/carousel';
import { PromoBonusCard } from 'components/promo-bonuses/promo-bonus-card/promo-bonus-card';
import { IconArrowLeft } from 'components/icons/icon-arrow-left/icon-arrow-left';
import { withPixelRatio } from 'hocs/with-pixel-ratio';
import { getBackgroundStyles } from 'helpers/images';
import { HeadHelmet } from 'components/head-helmet/head-helmet';

import {
  MODAL_IDS, CURRENCY_SYMBOLS, BONUSES_ACTION_TYPES, PAGE_NAMES
} from '../../constants';

import './bonus.scss';

const bonusCarouselSettings = {
  arrows: true,
  slidesToScroll: 4,
  responsive: [
    {
      arrows: false,
      breakpoint: 719,
      settings: {
        slidesToShow: 1.03,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 1279,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 1800,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 9999,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
      },
    },
  ],
};

class BonusUI extends Component {
  static propTypes = {
    isUserLoggedIn: PropTypes.bool.isRequired,
    isSignUpCompleted: PropTypes.bool.isRequired,
    isUserRegistered: PropTypes.bool.isRequired,
    setPendingBonus: PropTypes.func.isRequired,
    openModalAndCloseOthers: PropTypes.func.isRequired,
    defaultCurrency: PropTypes.string.isRequired,
    userTrackingCodes: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    pixelRatio: PropTypes.string.isRequired,
    userData: PropTypes.shape(),
    locale: PropTypes.string.isRequired,
    match: PropTypes.shape().isRequired,
  };

  static defaultProps = {
    userData: {},
  };

  state = {
    bonusItem: {},
    otherBonusItems: [],
    isNotFound: false,
    isTermsOpen: false,
    userData: {},
  };

  static getDerivedStateFromProps(props, state) {
    const {
      match: {
        params: { id: commonId },
      },
      items: bonuses,
      userData,
      userTrackingCodes,
    } = props;

    const { bonusItem, userData: prevUserData } = state;

    const targetCommonId = Number(commonId);

    if (
      targetCommonId
      && !R.isNil(bonuses)
      && (
        isEmptyOrNil(bonusItem)
        || targetCommonId !== R.prop('commonId', bonusItem)
        || !R.equals(userData, prevUserData)
      )
    ) {
      const isAffiliate = getIsAffiliate(userData, userTrackingCodes);
      const type = isAffiliate ? 'affiliate' : 'common';

      const targetBonuses = R.compose(
        R.reject(isEmptyOrNil),
        R.map(({ items = {} }) => (
          items[type] || items.common
        ))
      )(bonuses || []);

      const targetBonusItem = R.find(R.propEq('commonId', targetCommonId))(targetBonuses);
      const otherBonusItems = R.reject(R.propEq('commonId', targetCommonId))(targetBonuses);

      if (isEmptyOrNil(targetBonusItem)) {
        return {
          isNotFound: true,
        };
      }

      return {
        bonusItem: targetBonusItem,
        otherBonusItems,
        isTermsOpen: false,
        userData,
      };
    }

    return null;
  }

  toggleTerms = () => {
    this.setState(({ isTermsOpen }) => ({
      isTermsOpen: !isTermsOpen,
    }));
  };

  openModal = () => {
    const {
      isUserRegistered,
      isUserLoggedIn,
      isSignUpCompleted,
      openModalAndCloseOthers,
    } = this.props;

    let modalId;

    if (!isUserRegistered && !isUserLoggedIn) {
      modalId = MODAL_IDS.SIGN_UP;
    } else if (isUserRegistered && !isUserLoggedIn) {
      modalId = MODAL_IDS.SIGN_IN;
    } else if (isUserLoggedIn && !isSignUpCompleted) {
      modalId = MODAL_IDS.COMPLETE_SIGN_UP;
    } else if (isUserLoggedIn && isSignUpCompleted) {
      modalId = MODAL_IDS.DEPOSIT;
    }

    openModalAndCloseOthers(modalId);
  };

  activateBonus = () => {
    const {
      isUserLoggedIn,
      isSignUpCompleted,
      setPendingBonus,
    } = this.props;

    const {
      bonusItem: {
        bonusId,
        minDeposit,
      },
    } = this.state;

    const bonusPlanId = this.getValueFromCurrencies(bonusId);
    const minAmount = this.getValueFromCurrencies(minDeposit);

    if (bonusPlanId && minAmount) {
      if (isUserLoggedIn && isSignUpCompleted) {
        setPendingBonus({
          bonusPlanId,
          minAmount,
        });
      }

      this.openModal();
    }
  };

  getValueFromCurrencies = (obj) => {
    const { isUserLoggedIn, userData, defaultCurrency } = this.props;
    const userCurrency = isUserLoggedIn ? userData.currency : defaultCurrency;

    return R.path(['currencies', userCurrency])(obj);
  };

  render() {
    const {
      isUserLoggedIn,
      userData,
      defaultCurrency,
      pixelRatio,
      locale,
      match: {
        params: { id },
      },
    } = this.props;

    const {
      bonusItem,
      otherBonusItems,
      isNotFound,
      isTermsOpen,
    } = this.state;

    if (isNotFound) {
      const redirectUrl = wrapRoute2Locale('/promos', locale);

      return <Redirect to={redirectUrl} />;
    }

    if (isEmptyOrNil(bonusItem)) {
      return null;
    }

    const userCurrency = isUserLoggedIn ? userData.currency : defaultCurrency;
    const currencyCode = CURRENCY_SYMBOLS[userCurrency];

    const {
      title,
      buttonTitle,
      description,
      backgroundImageUrl,
      backgroundImageUrlTablet,
      backgroundImageUrlMobile,
      buttonTitleColor,
      buttonColor,
      // sortOrderIndex,
      imageUrl,
      imageUrlMobile,
      imageUrlTablet,
      buttonUrl,
      actionType,
      isNewTabLink,
    } = bonusItem;

    const backgroundStyles = getBackgroundStyles(backgroundImageUrl, pixelRatio, backgroundImageUrl);
    const backgroundTabletStyles = getBackgroundStyles(backgroundImageUrlTablet, pixelRatio, backgroundImageUrl);
    const backgroundMobileStyles = getBackgroundStyles(backgroundImageUrlMobile, pixelRatio, backgroundImageUrl);

    const characterStyles = getBackgroundStyles(imageUrl, pixelRatio, imageUrl);
    const characterTabletStyles = getBackgroundStyles(imageUrlTablet, pixelRatio, imageUrl);
    const characterMobileStyles = getBackgroundStyles(imageUrlMobile, pixelRatio, imageUrl);

    const sportsbookSchemeTitleIntlKey = `bonus.sportsbook.scheme.title.${id}`;
    const sportsbookSchemeIntlKey = `bonus.sportsbook.scheme.${id}`;

    const isBtnWithLink = buttonUrl && actionType === BONUSES_ACTION_TYPES.LINK;
    const isRelativeLink = (buttonUrl || '').startsWith('/');

    return (
      <>
        <HeadHelmet
          page={PAGE_NAMES.BONUS}
        />
        <Main>
          <div className="promo-bonus-page pt-1_25 pt-md-3">
            <RouteLink to="/promos" className="bonus-prev-link d-flex align-items-center mb-2 mb-md-3_75">
              <div className="bonus-prev-link-icon d-flex align-items-center justify-content-center">
                <IconArrowLeft />
              </div>

              <FormattedTag id="back" className="text-base text-capitalize text-white font-weight-bold ml-1_5 " />
            </RouteLink>

            {/* MOBILE */}
            <div
              style={backgroundMobileStyles}
              className="bonus-banner position-relative w-100 rounded mb-2 mb-md-3_75 w-100 h-100 d-sm-none"
            >
              <div
                style={characterMobileStyles}
                className="character-image position-absolute d-sm-none"
              />
            </div>

            {/* TABLET */}
            <div
              style={backgroundTabletStyles}
              className="bonus-banner position-relative w-100 rounded mb-2 mb-md-3_75 w-100 h-100 d-none d-sm-block d-md-none"
            >
              <div
                style={characterTabletStyles}
                className="character-image position-absolute d-none d-sm-block d-md-none"
              />
            </div>

            {/* DESKTOP */}
            <div
              style={backgroundStyles}
              className="bonus-banner position-relative w-100 rounded mb-2 mb-md-3_75 w-100 h-100 d-none d-md-block"
            >
              <div
                style={characterStyles}
                className="character-image position-absolute d-none d-md-block"
              />
            </div>

            <div className="d-flex justify-content-md-between align-items-center mb-1_5 mb-md-2_5">
              {title && (
                <div className="bonus-title h2 overflow-hidden pr-2 mb-0 primary font-weight-bold text-uppercase">
                  <FormattedTag
                    tag="span"
                    id={`bonus.read-more.${id}.title`}
                    values={{ amount: this.getValueFromCurrencies(title), currencyCode }}
                    isHtml
                  />
                </div>
              )}

              <Button
                {...(isBtnWithLink
                  ? {
                    tag: isRelativeLink ? RouteLink : 'a',
                    ...{ [isRelativeLink ? 'to' : 'href']: buttonUrl },
                    ...(isNewTabLink ? {
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    } : {}),
                  } : {
                    tag: Button,
                    onClick: this.activateBonus,
                    color: 'light',
                  }
                  )}
                className="bonus-card-button-panel-item bonus-btn d-none d-md-block flex-shrink-0"
                style={{ color: buttonTitleColor, background: buttonColor }}
              >
                <FormattedMessage
                  id={(buttonTitle && buttonTitle.intlKey) || 'get-bonus'}
                />
              </Button>
            </div>

            <div className="bonus-content">
              <div className="bonus-text text-base text-white mb-3_5 mb-md-5_5">
                <div className="bonus-desc mb-2 mb-md-2_5">
                  <FormattedHTMLMessage
                    id={`bonus.read-more.${id}.description`}
                    values={{ amount: this.getValueFromCurrencies(description), currencyCode }}
                  />

                  <FormattedHTMLMessage id="promo.bonuses.description">
                    {(desc) => {
                      const descContent = (desc || '').trim();

                      if (isEmptyOrNil(descContent) || descContent === 'promo.bonuses.description') {
                        return null;
                      }

                      const isContentForbidden = getIsHtmlContentForbidden({
                        key: 'promo.bonuses.description',
                        htmlTxt: descContent,
                      });

                      return (
                        <div dangerouslySetInnerHTML={{ __html: !isContentForbidden ? descContent : '' }} />
                      );
                    }}
                  </FormattedHTMLMessage>
                </div>

                <FormattedHTMLMessage id={sportsbookSchemeIntlKey}>
                  {(table) => {
                    const tableContent = (table || '').trim();

                    if (isEmptyOrNil(tableContent) || table === sportsbookSchemeIntlKey) {
                      return null;
                    }

                    return (
                      <div className="mb-2 mb-md-2_5">
                        <FormattedTag
                          id={sportsbookSchemeTitleIntlKey}
                          className="h4 bonus-scheme-title text-capitalize mb-2 d-block text-white"
                        />

                        <FormattedHTMLMessageSecure id={sportsbookSchemeIntlKey} />
                      </div>
                    );
                  }}
                </FormattedHTMLMessage>

                <div className="bonus-terms-collapse d-inline-block">
                  {/* eslint-disable-next-line */}
                <span
                  onClick={this.toggleTerms}
                  className="d-flex align-items-center cursor-pointer"
                >
                  <FormattedTag
                    id="bonus.title.brief"
                    className="h4 bonus-terms-title text-capitalize text-white text-weight-bold"
                  />

                  <IconArrowDown
                    color="white"
                    className={classNames('ml-1', {
                      'icon-down': isTermsOpen,
                    })}
                  />
                </span>

                  <Collapse isOpen={isTermsOpen} className="pt-1_5 h5 font-weight-normal letter-spacing-default">
                    <FormattedHTMLMessageSecure id={`bonus.brief.${id}`} />
                  </Collapse>
                </div>

                <Button
                  {...(isBtnWithLink
                    ? {
                      tag: isRelativeLink ? RouteLink : 'a',
                      ...{ [isRelativeLink ? 'to' : 'href']: buttonUrl },
                      ...(isNewTabLink ? {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      } : {}),
                    } : {
                      tag: Button,
                      onClick: this.activateBonus,
                      color: 'light',
                    }
                  )}
                  className="bonus-btn bonus-btn-bottom d-block d-md-none mt-3_5"
                >
                  {buttonTitle && <FormattedMessage id={getIntlKeyByEnv(buttonTitle.intlKey)} />}
                </Button>
              </div>

              <FormattedTag
                id="promo.other-bonuses"
                className="h2 d-block mt-2 mt-md-4_5 mb-3 mb-md-2_5 text-capitalize"
              />

              <Carousel settings={bonusCarouselSettings}>
                {!isEmptyOrNil(otherBonusItems) && otherBonusItems.map(item => (
                  <PromoBonusCard
                    key={item.id}
                    className="carousel-bonus-card overflow-auto"
                    item={item}
                    userCurrency={userCurrency}
                    currencyCode={CURRENCY_SYMBOLS[userCurrency]}
                    isCarouselBonus
                  />
                ))}
              </Carousel>
            </div>
          </div>
        </Main>
      </>
    );
  }
}

export default withCoreComponent(
  PromoBonusesCore,
  withLocale(
    withModalActions(
      withBonusActions(
        withPixelRatio(
          withUser(
            BonusUI,
            [
              USER_FIELDS.IS_USER_LOGGED_IN,
              USER_FIELDS.IS_SIGN_UP_COMPLETED,
              USER_FIELDS.IS_USER_REGISTERED,
              USER_FIELDS.USER_DATA,
              USER_FIELDS.DEFAULT_CURRENCY,
              USER_FIELDS.USER_TRACKING_CODES,
            ]
          )
        )
      ),
    )
  )
);
